import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { devicesService } from '../../services/devices';
import type { Device } from '../../types/device';
import { useCurrentGym } from '../hooks/useCurrentGym';
import { useGymAdmin } from '../context/gym-admin-context';
import DeviceFirmwareCard from '../components/DeviceFirmwareCard';
import DeviceBluetoothProxyCard from '../components/DeviceBluetoothProxyCard';

/**
 * One device: its code, its name, its Bluetooth proxy and the firmware it runs.
 * Boards are gym-owned and managed in the Boards section, so they no longer
 * live here.
 *
 * The code is shown but not editable — it is the device's identity and, on the
 * device's read endpoint, its credential, so it is generated rather than the
 * gym's to rewrite. It is copyable, because provisioning the hardware means
 * putting it into the device.
 */
export default function DeviceEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { placePath } = useGymAdmin();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { loading: gymLoading, slug } = useCurrentGym();

  const [device, setDevice] = useState<Device | null>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (gymSlug: string, deviceId: string) => {
    setLoading(true);
    setNotFound(false);
    try {
      const loaded = await devicesService.get(gymSlug, deviceId);
      setDevice(loaded);
      setName(loaded.name);
    } catch (error) {
      console.error('Failed to load device:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (slug && id) load(slug, id);
  }, [slug, id, load]);

  const canSave = name.trim().length > 0;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!slug || !device || !canSave || saving) return;

    setSaving(true);
    try {
      const updated = await devicesService.update(slug, device.id, { name: name.trim() });
      setDevice(updated);
      setName(updated.name);
      toast({ title: t('gymAdmin.devices.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.devices.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard?.writeText(value);
      toast({ title: t('gymAdmin.devices.copied') });
    } catch {
      // Clipboard access can be refused (insecure origin, denied permission).
      // The value is on screen either way, so this is not worth an error toast.
    }
  };

  if (gymLoading || loading) return <Skeleton className="h-96 w-full max-w-2xl" />;

  if (notFound || !device) {
    return (
      <div className="max-w-2xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(placePath('/devices'))}>
          <ArrowLeft className="mr-2 size-4" />
          {t('gymAdmin.devices.backToList')}
        </Button>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {t('gymAdmin.devices.notFound')}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(placePath('/devices'))}>
        <ArrowLeft className="mr-2 size-4" />
        {t('gymAdmin.devices.backToList')}
      </Button>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="device-name">{t('gymAdmin.devices.fields.name')}</Label>
              <Input id="device-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="device-code">{t('gymAdmin.devices.fields.code')}</Label>
              <div className="flex gap-2">
                <Input id="device-code" value={device.code} readOnly className="font-mono" />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(device.code)}
                  aria-label={t('gymAdmin.devices.copyCode')}
                >
                  <Copy className="size-4" />
                </Button>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              {t('gymAdmin.devices.credentialsHint')}
            </p>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => navigate(placePath('/devices'))}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={!canSave || saving}>
                {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* The proxy has a save of its own rather than riding the form above: it
          is a switch that changes what the box does in the room, and the field
          next to it is the device's name in the panel. */}
      {slug && (
        <DeviceBluetoothProxyCard gymSlug={slug} device={device} onDeviceChange={setDevice} />
      )}

      {slug && (
        <DeviceFirmwareCard gymSlug={slug} device={device} onDeviceChange={setDevice} />
      )}
    </div>
  );
}
